import { TAxiosResponse } from 'types';
import { axiosInstance } from './axiosInstance';
import { TCommonError } from '@models/common';
import { API_URL } from '@redux/rootConstant';
import {
  TGetAreaParam,
  TGetCompanyParam,
  TGetProcessingCenterParam,
  TGetSectorParam,
  TGetStoreParam,
} from 'types/types';
import {
  TGetMasterDataCompanyRes,
  TGetMasterDataProcessingCenterRes,
  TGetMasterDataSectorRes,
  TGetMasterDataStoreRes,
} from '@models/master';

const formDataType = 'multipart/form-data';

const masterDataApi = {
  getCompany: async (payload: TGetCompanyParam) => {
    const url = API_URL.MASTER_DATA.GET_COMPANY;
    const response: TAxiosResponse<TGetMasterDataCompanyRes[]> =
      await axiosInstance.get(url, {
        params: {
          storeId: payload.storeId,
        },
      });
    return response;
  },

  getSector: async (payload: TGetSectorParam) => {
    const url = API_URL.MASTER_DATA.GET_SECTOR;
    const response: TAxiosResponse<TGetMasterDataSectorRes[]> =
      await axiosInstance.get(url, {
        params: {
          storeId: payload.storeId,
          companyId: payload.companyId,
        },
      });
    return response;
  },

  getArea: async (payload: TGetAreaParam) => {
    const response: TAxiosResponse<TGetMasterDataSectorRes[]> =
      await axiosInstance.get(API_URL.MASTER_DATA.GET_SECTOR, {
        params: {
          companyId: payload.companyId,
          storeId: payload.storeId,
        },
      });
    return response;
  },

  getProcessingCenter: async (payload: TGetProcessingCenterParam) => {
    const response: TAxiosResponse<TGetMasterDataProcessingCenterRes[]> =
      await axiosInstance.get(API_URL.MASTER_DATA.GET_PROCESSING_CENTER, {
        params: {
          companyId: payload.companyId,
          sectorId: payload.sectorId,
          storeId: payload.storeId,
        },
      });
    return response;
  },

  getStore: async (payload: TGetStoreParam) => {
    const response: TAxiosResponse<TGetMasterDataStoreRes[]> =
      await axiosInstance.get(API_URL.MASTER_DATA.GET_STORE, {
        params: {
          companyId: payload.companyId || undefined,
          sectorId: payload.sectorId,
          companyCode: payload.companyCode || undefined,
        },
      });
    return response;
  },

  importCompany: async (payload: File) => {
    const url = API_URL.IMPORT_COMPANY;
    const formData = new FormData();
    formData.append('MasterCompanyFile', payload);
    const response: TAxiosResponse<any, TMasterDataErrorRes> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });
    return response;
  },

  importSector: async (payload: File) => {
    const url = API_URL.IMPORT_SECTOR;
    const formData = new FormData();
    formData.append('masterSectorFile', payload);

    const response: TAxiosResponse<
      TMasterDataDepartmentRes[],
      TMasterDataErrorRes
    > = await axiosInstance.post(url, formData, {
      headers: {
        'Content-type': formDataType,
      },
    });
    return response;
  },

  importProcessCenter: async (payload: File) => {
    const url = API_URL.IMPORT_PROCESSING_CENTER;
    const formData = new FormData();
    formData.append('masterProcessingCenterFile', payload);
    const response: TAxiosResponse<any, TMasterDataErrorRes> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });
    return response;
  },

  importArea: async (payload: File) => {
    const url = '/api/v1/area/import';
    const formData = new FormData();
    formData.append('masterAreaFile', payload);

    const response: TAxiosResponse<TMasterDataAreaRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importManufacturer: async (payload: File) => {
    const url = '/manufacturer/import';
    const formData = new FormData();
    formData.append('masterManufacturerFile', payload);

    const response: TAxiosResponse<TMasterDataManufacturerRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importPurpose: async (payload: File) => {
    const url = '/purpose/import';
    const formData = new FormData();
    formData.append('masterPurposeFile', payload);

    const response: TAxiosResponse<TMasterDataPurposeRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importUser: async (payload: File) => {
    const url = '/api/v1/user/import';
    const formData = new FormData();
    formData.append('masterUserFile', payload);

    const response: TAxiosResponse<TMasterDataUserRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
        timeout: 120000,
      });

    return response;
  },

  importStore: async (payload: File) => {
    const url = '/api/v1/store/import';
    const formData = new FormData();
    formData.append('masterStoreFile', payload);

    const response: TAxiosResponse<TMasterDataSensorRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importSensor: async (payload: File) => {
    const url = '/sensor/import';
    const formData = new FormData();
    formData.append('masterSensorFile', payload);

    const response: TAxiosResponse<TMasterDataSensorRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importDepartment: async (payload: File) => {
    const url = '/department/import';
    const formData = new FormData();
    formData.append('masterDepartmentFile', payload);

    const response: TAxiosResponse<TMasterDataDepartmentRes[], TCommonError> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });

    return response;
  },

  importCamera: async (payload: File) => {
    const url = API_URL.IMPORT_CAMERA;
    const formData = new FormData();
    formData.append('masterCameraFile', payload);
    const response: TAxiosResponse<any, TMasterDataErrorRes> =
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-type': formDataType,
        },
      });
    return response;
  },
};

export { masterDataApi };

type TMasterDataAreaRes = {
  areaCode: string;
  areaName: string;
};

type TMasterDataDepartmentRes = {
  departmentCode: 'string';
  departmentName: 'string';
};

type TMasterDataManufacturerRes = {
  code: 'string';
  name: 'string';
  description: 'string';
};

type TMasterDataPurposeRes = {
  id: '3fa85f64-5717-4562-b3fc-2c963f66afa6';
  purposeCode: 'string';
  purposeName: 'string';
  departmentCode: 'string';
  departmentName: 'string';
  companyCode: 'string';
  minTemp: 'string';
  maxTemp: 'string';
};

type TMasterDataSensorRes = {
  storeCode: 'string';
  storeName: 'string';
};

type TMasterDataUserRes = {
  userName: 'string';
  name: 'string';
  companyCode: 'string';
  storeCode: 'string';
  role: 'string';
};

type TMasterDataErrorRes = {
  code: string;
  description: string;
  rowIndex: number;
  columnName: string;
};
